import { useCreationStack } from '../context/CreationStackContext'
import { useCallback } from 'react'

export interface CreationStackStepProps {
  stackData: Record<string, any>
  onNext: () => void
  onBack: () => void
  onClose: () => void
  isLastStep: boolean
  isFirstStep: boolean
  completeStep: (stepData?: Record<string, any>) => void
  updateStackData: (data: Record<string, any>) => void
}

export const useCreationStackIntegration = () => {
  const {
    showCreationStack,
    hideCreationStack,
    currentStack,
    isVisible,
    completeStep,
    updateStackData,
    nextStep,
    goToStep,
    previousStep,
    isCreationStackEnabled,
  } = useCreationStack()

  // Helper function to show note creation modal
  const showNoteCreation = useCallback((initialData?: Record<string, any>) => {
    showCreationStack('note', initialData)
  }, [showCreationStack])

  // Helper function to show message creation modal
  const showMessageCreation = useCallback((initialData?: Record<string, any>) => {
    showCreationStack('message', initialData)
  }, [showCreationStack])

  // Helper function to show reminder creation modal
  const showReminderCreation = useCallback((initialData?: Record<string, any>) => {
    showCreationStack('reminder', initialData)
  }, [showCreationStack])

  //Helper function to show keep contact creation modal
  const showKeepContactCreation = useCallback((initialData?: Record<string, any>, stepIndex: number = 0) => {
    showCreationStack('keepContact', initialData, stepIndex)
  }, [showCreationStack])

  // Helper function to show scheduled message creation stack
  const showScheduledMessageCreation = useCallback((initialData?: Record<string, any>, stepIndex: number = 0) => {
    showCreationStack('scheduledMessage', initialData, stepIndex)
  }, [showCreationStack])

  // Helper function to complete current step and move to next
  const handleStepCompletion = useCallback((stepData?: Record<string, any>) => {
    completeStep(stepData)
  }, [completeStep])

  // Helper function to update current step data
  const handleDataUpdate = useCallback((data: Record<string, any>) => {
    updateStackData(data)
  }, [updateStackData])

  // Helper function to handle step navigation
  const handleStepNavigation = useCallback((direction: 'next' | 'back') => {
    if (direction === 'next') {
      nextStep()
    } else {
      previousStep()
    }
  }, [nextStep, previousStep])

  return {
    // Stack state
    currentStack,
    isVisible,
    isCreationStackEnabled,
    
    // Stack actions
    showNoteCreation,
    showMessageCreation,
    showReminderCreation,
    showKeepContactCreation,
    showScheduledMessageCreation,
    hideCreationStack,
    
    // Step actions
    handleStepCompletion,
    handleDataUpdate,
    handleStepNavigation,
    
    // Direct access to context methods
    completeStep,
    updateStackData,
    nextStep,
    previousStep,
    goToStep,
  }
}
